<template>
  <actionable-panel
    header-text="Review & submit order"
    :complete="complete"
    :active="active"
    :current-step="currentStep"
    :total-steps="totalSteps"
    :disable-edit="editDisabled"
    @edit="transitionEdit"
  >
    <expand-transition slot="table">
      <div v-if="pricingGroup && pricingGroup.id" class="alert-message">
        <SbBanner variation="warning">
          <p class="sb-banner__text">
            Shipping labels are non-refundable.
          </p>
        </SbBanner>
      </div>
    </expand-transition>
    <expand-transition slot="table">
      <div class="wro-actionable-panel__body">
        <div class="wro-confirm-shipping">
          <h4 class="wro-confirm-shipping__block-title">
            Shipping Details:
          </h4>
          <div class="wro-confirm-shipping__details-wrap">
            <div class="wro-confirm-shipping__address">
              <p data-sb-automation="address" class="wro-confirm-shipping__address-line">
                {{ companyName }} <span v-if="!isPoweredBySubAccountUser">C/O ShipBob, Inc</span>
              </p>
              <p class="wro-confirm-shipping__address-line">
                {{ fulfillmentCenterAddress.address1 }}
              </p>
              <p class="wro-confirm-shipping__address-line">
                {{ fulfillmentCenterAddress.address2 }}
              </p>
              <p class="wro-confirm-shipping__address-line">
                {{ fulfillmentCenterAddress.city }}, {{ fulfillmentCenterAddress.state }}, {{ fulfillmentCenterAddress.zipCode }}
              </p>
              <p class="wro-confirm-shipping__address-line">
                Phone: {{ fulfillmentCenterAddress.phoneNumber | phone }}
              </p>
              <p v-if="!isPoweredBySubAccountUser" data-sb-automation="email" class="wro-confirm-shipping__address-line">
                Email: {{ fulfillmentCenterAddress.email }}
              </p>
            </div>
            <div class="wro-confirm-shipping__package-info">
              <template v-if="checkShippingType">
                <p class="wro-confirm-shipping__package-info-line">
                  Shipping type: Parcel
                </p>
              </template>
              <template v-else>
                <p class="wro-confirm-shipping__package-info-line">
                  Shipping type: Freight
                </p>
              </template>
              <p class="wro-confirm-shipping__package-info-line">
                # of boxes: {{ totalNumberOfBoxes }}
              </p>
              <p class="wro-confirm-shipping__package-info-line">
                Configuration: {{ skuType }}
              </p>
              <p class="wro-confirm-shipping__package-info-line selectedLabel">
                Labels: {{ label }}
              </p>
              <p v-if="pricingGroup && pricingGroup.id" class="wro-confirm-shipping__package-info-line">
                Label Cost:
                <span v-if="showLocalCurrencyWithSymbolAndCode" class="estimatedShippingCost">
                  {{ estimatedShippingCost | formatCurrency({currency: shippingCurrencyCode, showCurrencyCode: true}) }}
                </span>
                <span v-else class="estimatedCost"> {{ estimatedShippingCost }} </span>
              </p>
              <p class="wro-confirm-shipping__package-info-line">
                Estimated Arrival Date: {{ estimatedArrivalDate | date('MM/DD/YYYY') | noValue }}
              </p>
              <p class="wro-confirm-shipping__package-info-line">
                PO #: {{ purchaseOrderNumber | noValue }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </expand-transition>
    <expand-transition slot="table">
      <sb-table
        v-if="hasSelectedProducts"
        :column-definitions="columnDefinitions"
        :rows="selectedProducts"
      >
        <sb-table-thead slot="sb-table-header" slot-scope="{cols}">
          <sb-table-head>
            <div :class="[selectedProductsHasDangerous ? 'table-header-adjustment-icon':'']">
              {{ cols[0].name }}
            </div>
          </sb-table-head>
          <sb-table-head>{{ cols[1].name }}</sb-table-head>
          <sb-table-head>{{ cols[2].name }}</sb-table-head>
          <sb-table-head>{{ cols[3].name }}</sb-table-head>
        </sb-table-thead>
        <sb-table-row slot="sb-table-row" slot-scope="{row}">
          <sb-table-cell>
            <span v-if="row.isDangerousGoods" class="icon-alignment">
              <dangerous-icon class="dangerous_icon" />
              <sb-tooltip class="dangerous-goods__tooltip" orientation="bottom">
                <p class="dangerous-goods__tooltip-title">Dangerous goods</p>
              </sb-tooltip>
            </span>
            <div :class="[selectedProductsHasDangerous ? 'table-data-adjustment-icon':'']">
              <router-link
                :to="{ name: productsRoute, params: { productId: row.id } }"
                target="_blank"
              >
                {{ row.id }}
              </router-link>
            </div>
          </sb-table-cell>
          <sb-table-cell>{{ row.name }}</sb-table-cell>
          <sb-table-cell>{{ row.sku }}</sb-table-cell>
          <sb-table-cell>
            <div class="quantities-to-send__row">
              {{ summateQuantitiesToSend(row.quantitiesToSend) }}
            </div>
          </sb-table-cell>
        </sb-table-row>
      </sb-table>
    </expand-transition>
    <div slot="footer" class="wro-actionable-panel__footer">
      <div v-if="isWroCreated && isUnlinkUROAtWROEdit && uroIds.length > 0" class="wro-actionable-panel__footer__checkbox">
        <SbBanner variation="danger" class="alter-prod-warning">
          <sb-checkbox v-model="checkForAcknowledgementOfUnlinkedUro" data-sb-automation="acknowledge-unlink-uro">
            <p class="sb-banner__text">
              This Warehouse Receiving Order is currently linked to UnIdentified Receiving Order(s):
              {{ uroIds.length > 30 ? uroIds.slice(0,30) + ", and others" : uroIds }}. Edits to the Warehouse Receiving Order will
              result in it being unlinked from the Unidentified Receiving Order(s). After edits have been made to the Warehouse
              Receiving Order, please navigate to the <a v-if="isValidUrl(onHoldReceivingPage)" target="_blank" :href="onHoldReceivingPage">On Hold Receiving</a> page
              and re-link the URO(s) to the WRO box(s).
            </p>
          </sb-checkbox>
        </SbBanner>
      </div>
      <div v-if="pricingGroup && pricingGroup.id" class="save-bar-submit">
        <sb-button :disabled="isDemoUser" variation="secondary" @click="submitDraftWro">
          Purchase Labels & Submit
        </sb-button>
      </div>
      <div v-else class="save-bar-submit">
        <sb-button :disabled="isDemoUser || (isUnlinkUROAtWROEdit && uroIds.length > 0 && (!checkForAcknowledgementOfUnlinkedUro && isWroCreated))" variation="secondary" @click="submitDraftWro">
          {{ isWroCreated ? 'Submit Changes' : 'Submit Receiving Order' }}
        </sb-button>
      </div>
    </div>
  </actionable-panel>
</template>

<script>
import ShipbobAppSettings from '@/appSettings/appSetting'
import stepsMixin from './stepsMixin'
import { ProductsEnum, WarehouseReceivingOrderEnum } from '@/enums/modules/routes'
import _sumBy from 'lodash/sumBy'
import _flatMap from 'lodash/flatMap'
import { skuTypeEnum } from '../../../enums/modules/creatingWarehouseReceivingOrder/skuType'

import ServiceTypes from '@/enums/modules/fulfillmentCenter/serviceType.js'

const columnDefinitions = [
  { name: 'Inventory ID', field: 'id' },
  { name: 'Item Name', field: 'name' },
  { name: 'SKU', field: 'sku' },
  { name: 'Quantity Sending', field: 'quantity' }
]

export default {
  name: 'ReviewAndSubmit',
  components: {},
  filters: {
    phone (val = '') {
      const p = val.match(/(\d{3})(\d{3})(\d{4})$/)
      if (p) return `(${p[1]}) ${p[2]}-${p[3]}`
      return val
    }
  },
  mixins: [stepsMixin],
  inject: ['isPoweredBySubAccountUser'],
  props: {},
  data: () => ({
    columnDefinitions,
    productsRoute: ProductsEnum.ProductDetails,
    createWroModal: WarehouseReceivingOrderEnum.CreateWroConfirmation,
    label: 'Your Own Label',
    onHoldReceivingPage: `${ShipbobAppSettings.photoPrint}/App/IndexV2/#/OnHoldReceiving`,
    checkForAcknowledgementOfUnlinkedUro: false
  }),
  computed: {
    isDemoUser () {
      // checking for demo user
      return this.$shipBobStore.getters['user/isDemoUser']
    },
    checkShippingType () {
      return this.shippingType.id === 1
    },
    hasSelectedProducts () {
      return this.selectedProducts.length > 0
    },
    user () {
      return this.$shipBobStore.getters['user/getUser']
    },
    companyName () {
      return this.user.impersonatedCompanyName || this.user.companyName.value
    },
    selectedProductsHasDangerous () {
      return this.selectedProducts.some(x => x.isDangerousGoods === true)
    },
    selectedProducts: {
      get () {
        return this.$shipBobStore.getters[this.StoreModuleName + '/selectedProducts'] || []
      },
      set (n) {
        this.$shipBobStore.dispatch(this.StoreModuleName + '/setSelectedProducts', n)
      }
    },
    fulfillmentCenterAddress () {
      const receivingService = this.draft && this.draft.fulfillmentCenter && this.draft.fulfillmentCenter.getService(ServiceTypes.Receiving)
      return (receivingService && receivingService.address) || {}
    },
    totalNumberOfBoxesPerSingleSku () {
      return _sumBy(_flatMap(this.draft.shippingConfigurations.map(sc => sc.boxConfigurations.map(bc => bc))), o => o.numberOfBoxes)
    },
    totalNumberOfBoxesPerMultiSku () {
      return _sumBy(this.draft.multiSKUShippingConfigurations, o => o.skuConfigurations.length > 0 ? o.skuConfigurations[0].numberOfBoxes : 0)
    },
    totalNumberOfBoxes () {
      return (this.draft.skuType && this.draft.skuType.id === skuTypeEnum.OneSKUPerContainer) ? this.totalNumberOfBoxesPerSingleSku : this.totalNumberOfBoxesPerMultiSku
    },
    shippingType () {
      return this.draft ? this.draft.packagingType || {} : {}
    },
    skuType () {
      return (this.draft.skuType && this.draft.skuType.id === skuTypeEnum.OneSKUPerContainer) ? 'Single SKU per box' : 'Multiple SKU\'s per box'
    },
    isShipBobLabels () {
      return this.$shipBobStore.getters[this.StoreModuleName + '/isShipBobLabels']
    },
    estimatedShippingCost () {
      return this.showLocalCurrencyWithSymbolAndCode
        ? this.pricingGroup.estimatedShippingCost
        : this.$n(this.pricingGroup.estimatedShippingCost, 'currency')
    },
    shippingCurrencyCode () {
      return this.pricingGroup.currencyCode
    },
    pricingGroup () {
      return this.$shipBobStore.getters[this.StoreModuleName + '/pricingGroup']
    },
    purchaseOrderNumber () {
      return this.draft.purchaseOrderNumber
    },
    estimatedArrivalDate () {
      return this.draft.estimatedArrivalDate
    },
    enableWROByShipOption () {
      return this.$shipBobStore.getters.enableWROByShipOption
    },
    showLocalCurrencyWithSymbolAndCode () {
      return this.enableWROByShipOption &&
      this.$shipBobStore.getters.showLocalCurrencyWithSymbolAndCode
    },
    isUnlinkUROAtWROEdit () {
      return this.$shipBobStore.getters['Inbound.UnlinkUROAtWROEdit']
    },
    uroIds () {
      return this.draft.uroIds
    }
  },
  watch: {
    pricingGroup: {
      handler (n, o) {
        if (n && n.id) {
          this.label = n.name
        } else this.label = 'Your Own Label'
      }
    }
  },
  destroyed () {},
  methods: {
    isValidUrl (url) {
      try {
        const parsedUrl = new URL(url)
        return ['http:', 'https:'].includes(parsedUrl.protocol)
      } catch (e) {
        return false
      }
    },
    submitDraftWro () {
      !this.isWroCreated
        ? this.$router.push({ name: WarehouseReceivingOrderEnum.CreateWroConfirmation })
        : this.$router.push({ name: WarehouseReceivingOrderEnum.EditWroConfirmation })
    },
    summateQuantitiesToSend (qts) {
      return qts.reduce((acc, n) => {
        acc += n.amount
        return acc
      }, 0)
    }
  }
}
</script>

<style scoped lang='scss'>
  @import './panel.scss';
  .alert-message {
    margin: 0rem 2rem 0rem 2rem
  }
  .table-header-adjustment-icon {
    margin-left: 0.5rem;
  }
  .table-data-adjustment-icon {
    margin-left: 1rem;
  }
  .icon-alignment {
    position: absolute;
    left: 15px;
    height: 21px;
    display: flex;
    align-items: center;
  }
  .dangerous_icon {
    margin-right: 7px;
    width: 15px;
    height: 15px;
    float: left;
  }
  .dangerous-goods {
    &__tooltip {
      padding: 1em;
      max-width: 20em;
    }
    &__tooltip-title {
      font-weight: 600;
    }
    &__tooltip-message, &__tooltip-title {
      display: inline;
    }
  }
  .sb-banner {
    margin-bottom: 1rem;
    &__text {
      margin: 0;
    }
  }
  .wro-actionable-panel__body {
    width: 50%;
    margin: 0rem 0rem 2rem 5rem;
    .wro-confirm-shipping {
      margin-bottom: 2em;
      &:last-of-type {
        margin-bottom: 0;
      }
      &__block-title {
        color: #4e5c73;
        margin: 0;
        &--space-bottom {
          margin-bottom: 1em;
        }
      }
      &__address {
        color: #727a87;
      }
      &__address-line {
        margin: 0;
      }
      &__package-info {
        color: #727a87;
      }
      &__package-info-line {
        margin: 0;
      }
      &__cancel {
        margin-left: 1rem;
      }
      &__details-wrap {
        display: flex;
        flex-flow: row wrap;
        flex-wrap: wrap;
        justify-content: space-between;
      }
      &__date-picker {
        display: inline-block;
        color: #2470af;
      }
      &__file-upload-link-container {
        display: inline-flex;
        align-items: center;
        margin: 0;
        margin-top: 1em;
        max-width: 100%;
      }
      &__file-upload-link {
        color: #2470af;
        font-weight: 600;
        cursor: pointer;
      }
      &__file-upload-link-icon {
        margin-right: 0.5em;
        font-size: 1.25em;
      }
      &__file-upload-link-text {
        margin: 0;
      }
    }
  }
.wro-actionable-panel__footer__checkbox {
  display: flex;
}
</style>
